// CodingInterviewStage — conversational technical interview.
//
//   ┌──────────────────────────┬──────────────────────────────────────┐
//   │  AGENT (left)            │  CODE EDITOR (right)                 │
//   │  - Avatar + state ring   │  - Language picker                   │
//   │  - "Click Start"         │  - Monaco editor                     │
//   │  - "Listening / Speaking"│  - Run + output                      │
//   └──────────────────────────┴──────────────────────────────────────┘
//
// The agent (ElevenLabs Conv AI) drives the conversation. The editor is the
// candidate's working surface. The agent can read the code on demand via
// the `read_candidate_code` client tool (registered below); the candidate
// can run their code via the Run button.

import React, { useEffect, useRef, useState, useCallback } from "react";
import Editor from "@monaco-editor/react";
import { Conversation } from "@elevenlabs/client";
import { Play, Mic, Brain, Volume2, Loader2, MessageSquare } from "lucide-react";
import { useProctorGuard, ProctorWarningOverlay, ProctorBadge } from "../common/ProctorGuard";

const STATE = {
  IDLE: "idle",
  CONNECTING: "connecting",
  LISTENING: "listening",
  SPEAKING: "speaking",
  THINKING: "thinking",
  ENDING: "ending",
  DONE: "done",
};

const _envBase = process.env.REACT_APP_API_URL || "";
const API_BASE =
  (typeof window !== "undefined" && window.__API_BASE__) ||
  (_envBase ? _envBase.replace(/\/api\/?$/, "") : "");

const LANG_MONACO = {
  python: "python", javascript: "javascript", typescript: "typescript",
  java: "java", cpp: "cpp", go: "go",
};
const LANG_LABEL = {
  python: "Python", javascript: "JavaScript", typescript: "TypeScript",
  java: "Java", cpp: "C++", go: "Go",
};
const STARTER = {
  python: "# Type your code here.\ndef solve():\n    pass\n\nsolve()\n",
  javascript: "// Type your code here.\nfunction solve() {\n}\nsolve();\n",
  typescript: "// Type your code here.\nfunction solve(): void {\n}\nsolve();\n",
  java: "// Type your code here.\nSystem.out.println(\"Hello\");\n",
  cpp: "#include <bits/stdc++.h>\nusing namespace std;\nint main(){\n    return 0;\n}\n",
  go: "package main\nimport \"fmt\"\nfunc main(){\n    fmt.Println(\"Hello\")\n}\n",
};

export default function CodingInterviewStage({ jobId, resumeId, stageId = "", candidateName = "there", onComplete }) {
  const [state, setState]         = useState(STATE.IDLE);
  const [error, setError]         = useState("");
  const [language, setLanguage]   = useState("python");
  const [code, setCode]           = useState(STARTER.python);
  const [output, setOutput]       = useState(null);   // { stdout, stderr, exitCode, runtimeMs }
  const [running, setRunning]     = useState(false);
  const [endedReason, setEndedReason] = useState("");

  // Mirrors the latest editor content + most recent run for the agent's tools.
  // Refs so the client-tool callbacks always read fresh values without re-registering.
  const codeRef          = useRef(code);
  const lastRunRef       = useRef(null);
  const conversationRef  = useRef(null);
  const conversationIdRef = useRef(null);
  const finishedRef      = useRef(false);
  const startTimeRef     = useRef(0);

  const isActive = [STATE.CONNECTING, STATE.LISTENING, STATE.SPEAKING, STATE.THINKING].includes(state);

  // ── Proctoring ─────────────────────────────────────────────────────
  const { warnings, showWarning, warningMsg, dismissWarning } = useProctorGuard({
    active: isActive,
    onViolation: (reason, count) => console.warn(`[proctor/coding] violation ${count}: ${reason}`),
    onEnd: (reason) => finishCall(reason),
  });

  useEffect(() => { codeRef.current = code; }, [code]);

  // ── Periodic snapshot (every 30s) so HR can scrub through the timeline ──
  useEffect(() => {
    if (state !== STATE.LISTENING && state !== STATE.SPEAKING && state !== STATE.THINKING) return;
    const t = setInterval(() => {
      _postSnapshot(codeRef.current, "interval");
    }, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  // Tab-switch lockdown handled by useProctorGuard above.

  // ── Helpers ───────────────────────────────────────────────────────
  async function _postSnapshot(content, reason) {
    try {
      await fetch(`${API_BASE}/api/public/coding/snapshot`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ resumeId, jobId, language, code: content, reason }),
      });
    } catch (e) { /* best effort */ }
  }

  async function requestMic() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      return true;
    } catch (e) {
      setError("Microphone access is required for the interview. " + (e.message || ""));
      return false;
    }
  }

  // ── Run code ──────────────────────────────────────────────────────
  const runCode = useCallback(async () => {
    setRunning(true);
    setOutput(null);
    try {
      const r = await fetch(`${API_BASE}/api/public/coding/run`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ resumeId, jobId, language, code: codeRef.current }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || "Run failed");
      setOutput(data);
      lastRunRef.current = { ...data, language, code: codeRef.current, ts: new Date().toISOString() };
    } catch (e) {
      setOutput({ stdout: "", stderr: e.message, exitCode: -1, runtimeMs: 0 });
      lastRunRef.current = { error: e.message };
    } finally {
      setRunning(false);
    }
  }, [language, resumeId, jobId]);

  // ── Start the agent session ────────────────────────────────────────
  async function begin() {
    setError("");
    const ok = await requestMic();
    if (!ok) return;

    setState(STATE.CONNECTING);
    try {
      const r = await fetch(`${API_BASE}/api/public/agent/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ round: "coding", jobId, candidateId: resumeId, stageId }),
      });
      const startData = await r.json();
      if (r.status === 403 && startData.alreadyCompleted) {
        setError("You have already completed this interview. Each round can only be attempted once.");
        setState(STATE.DONE);
        finishedRef.current = true;
        return;
      }
      if (r.status === 503 && startData.missingKey) {
        setError("Voice interviewer not available — ElevenLabs API key not configured. You can still use the code editor below.");
        setState(STATE.IDLE);
        return;
      }
      if (!r.ok) throw new Error(startData.message || "Failed to start agent session");

      startTimeRef.current = Date.now();

      const conversation = await Conversation.startSession({
        signedUrl: startData.signedUrl,
        dynamicVariables: startData.dynamicVariables,

        // ── Client tools the agent can invoke ──
        clientTools: {
          read_candidate_code: () => ({
            language,
            code: codeRef.current,
            chars: codeRef.current.length,
          }),
          read_last_run: () => lastRunRef.current || { note: "No code has been run yet." },
        },

        onConnect: ({ conversationId }) => {
          conversationIdRef.current = conversationId;
          setState(STATE.LISTENING);
        },
        onDisconnect: () => {
          if (!finishedRef.current) finishCall("disconnect");
        },
        onError: (e) => {
          console.error("[coding agent] error:", e);
          setError(typeof e === "string" ? e : (e?.message || "Agent error"));
        },
        onModeChange: ({ mode }) => {
          if (mode === "speaking") setState(STATE.SPEAKING);
          else if (mode === "listening") setState(STATE.LISTENING);
        },
        onStatusChange: ({ status }) => {
          if (status === "disconnected" && !finishedRef.current) finishCall("status_disconnect");
        },
      });

      conversationRef.current = conversation;
    } catch (e) {
      console.error("coding agent/start failed:", e);
      setError(e.message || "Could not start the agent");
      setState(STATE.IDLE);
    }
  }

  async function finishCall(reason = "user_end") {
    if (finishedRef.current) return;
    finishedRef.current = true;
    setState(STATE.ENDING);
    setEndedReason(reason);

    try { await conversationRef.current?.endSession(); } catch {}

    const durationSec = (Date.now() - startTimeRef.current) / 1000;

    // Save a final snapshot
    await _postSnapshot(codeRef.current, "done");

    if (conversationIdRef.current) {
      try {
        const r = await fetch(`${API_BASE}/api/public/agent/finish`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            conversationId: conversationIdRef.current,
            round: "coding", jobId, candidateId: resumeId, stageId, durationSec,
          }),
        });
        const data = await r.json();
        setState(STATE.DONE);
        onComplete?.(data);
      } catch (e) {
        setState(STATE.DONE);
        onComplete?.({ error: e.message });
      }
    } else {
      setState(STATE.DONE);
      onComplete?.({ aborted: true });
    }
  }

  useEffect(() => () => { try { conversationRef.current?.endSession?.(); } catch {} }, []);

  // When the candidate switches language, swap the starter only if editor is empty/default
  function changeLanguage(next) {
    setLanguage(next);
    if (!codeRef.current || Object.values(STARTER).some((s) => s.trim() === codeRef.current.trim())) {
      setCode(STARTER[next]);
    }
    _postSnapshot(codeRef.current, "language_change");
  }

  // ── UI ─────────────────────────────────────────────────────────────
  const stateText = {
    [STATE.IDLE]:       "Tap Start when you're ready",
    [STATE.CONNECTING]: "Connecting to interviewer…",
    [STATE.SPEAKING]:   "Alex is speaking…",
    [STATE.LISTENING]:  "Listening — speak naturally",
    [STATE.THINKING]:   "Thinking…",
    [STATE.ENDING]:     "Wrapping up…",
    [STATE.DONE]:       "Interview complete",
  }[state];

  const ringClass =
    state === STATE.SPEAKING   ? "ring-meraki-indigo animate-pulse" :
    state === STATE.LISTENING  ? "ring-meraki-coral animate-pulse" :
    state === STATE.CONNECTING ? "ring-amber-400 animate-pulse" :
    state === STATE.ENDING     ? "ring-slate-400" :
                                 "ring-slate-300";

  const Icon =
    state === STATE.SPEAKING ? Volume2 :
    state === STATE.LISTENING ? Mic :
    state === STATE.THINKING ? Brain :
    state === STATE.CONNECTING ? Loader2 :
                                 MessageSquare;

  const canEnd = [STATE.LISTENING, STATE.SPEAKING, STATE.THINKING].includes(state);

  return (
    <>
    <ProctorWarningOverlay show={showWarning} message={warningMsg} warnings={warnings} maxWarnings={3} onDismiss={dismissWarning} />
    {isActive && <ProctorBadge warnings={warnings} maxWarnings={3} />}
    <div className="h-screen w-full bg-meraki-cloud flex font-outfit text-meraki-graphite">
      {/* ── Left: Agent ── */}
      <aside className="w-[380px] bg-white border-r border-meraki-soft-blue flex flex-col p-6">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest text-meraki-indigo/60">Coding interview</div>
          <h1 className="title-meraki text-xl mt-1">Alex · Staff Engineer</h1>
          <p className="subtitle-meraki text-sm mt-1">A conversational session — code as we chat.</p>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center text-center">
          <div className={`w-40 h-40 rounded-full bg-meraki-cloud shadow-meraki-card ring-8 ${ringClass} flex items-center justify-center transition-all`}>
            <Icon size={56} strokeWidth={2.2} className={`text-meraki-indigo ${state === STATE.CONNECTING ? "animate-spin" : ""}`} />
          </div>
          <div className="mt-6 text-lg font-bold text-meraki-indigo">{stateText}</div>
          {error && <div className="mt-3 text-sm text-meraki-coral max-w-xs">{error}</div>}

          {state === STATE.IDLE && (
            <button onClick={begin} className="btn-meraki mt-8">
              <Mic size={18} strokeWidth={2.5} />
              Start interview
            </button>
          )}

          {canEnd && (
            <button onClick={() => finishCall("user_end")} className="btn-meraki-ghost mt-6 text-sm">
              End interview
            </button>
          )}

          {state === STATE.DONE && (
            <div className="mt-6 text-sm text-meraki-graphite/60">
              Thank you — the hiring team will be in touch. {endedReason && <span className="opacity-50">({endedReason})</span>}
            </div>
          )}
        </div>

        <div className="text-xs text-meraki-graphite/40 mt-4 text-center">
          The interviewer can see your code when they ask about it. Switching tabs auto-ends the call.
        </div>
      </aside>

      {/* ── Right: Editor + Output ── */}
      <section className="flex-1 flex flex-col">
        {/* Top bar — language + run */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-meraki-soft-blue bg-white">
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-meraki-graphite/70">Language</span>
            <select
              value={language}
              onChange={(e) => changeLanguage(e.target.value)}
              disabled={state !== STATE.IDLE && state !== STATE.LISTENING && state !== STATE.SPEAKING && state !== STATE.THINKING}
              className="px-3 py-2 border border-meraki-soft-blue rounded-meraki font-medium text-sm bg-meraki-cloud focus:ring-2 focus:ring-meraki-indigo focus:outline-none"
            >
              {Object.entries(LANG_LABEL).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <button
            onClick={runCode}
            disabled={running || state === STATE.IDLE || state === STATE.DONE}
            className="btn-meraki text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {running ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} strokeWidth={2.5} />}
            {running ? "Running…" : "Run"}
          </button>
        </div>

        {/* Editor */}
        <div className="flex-1 min-h-0">
          <Editor
            height="100%"
            language={LANG_MONACO[language]}
            value={code}
            onChange={(v) => setCode(v ?? "")}
            theme="vs-light"
            options={{
              fontSize: 14,
              fontFamily: "'Fira Code', 'JetBrains Mono', Menlo, Consolas, monospace",
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              tabSize: 2,
              wordWrap: "on",
              automaticLayout: true,
              padding: { top: 16 },
            }}
          />
        </div>

        {/* Output */}
        <div className="h-[200px] bg-meraki-indigo text-meraki-cloud font-mono text-xs overflow-auto px-6 py-3 border-t border-meraki-indigo/40">
          {output ? (
            <>
              <div className="text-meraki-coral font-bold mb-2">
                exit {output.exitCode} · {output.runtimeMs}ms
              </div>
              {output.stdout && <pre className="whitespace-pre-wrap">{output.stdout}</pre>}
              {output.stderr && <pre className="whitespace-pre-wrap text-red-300 mt-2">{output.stderr}</pre>}
              {!output.stdout && !output.stderr && <pre className="opacity-50">(no output)</pre>}
            </>
          ) : (
            <div className="opacity-50">Output will appear here when you click Run.</div>
          )}
        </div>
      </section>
    </div>
    </>
  );
}
